// EventDetail.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EventContext } from '../context/EventContext';
import { AuthContext } from '../context/AuthContext';
import { getUserProfile } from '../api/users';
import { removeParticipant } from '../api/events';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getEvent, registerForEvent } = useContext(EventContext);
  const { user, userData, isAdmin } = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isParticipating, setIsParticipating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [participantsWithNames, setParticipantsWithNames] = useState([]);
  const [removeDialog, setRemoveDialog] = useState({ show: false, userId: null, userName: '' });

  useEffect(() => {
    fetchEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventData = await getEvent(id);
      setEvent(eventData);

      // Check if user is already participating
      if (user && eventData.participants) {
        const participating = eventData.participants.some(
          (participant) => participant.userId === user.uid
        );
        setIsParticipating(participating);
      }

      // Fetch participant names for admin
      if (isAdmin && eventData.participants && eventData.participants.length > 0) {
        const participantsWithDetails = await Promise.all(
          eventData.participants.map(async (participant) => {
            try {
              const userProfile = await getUserProfile(participant.userId);
              // Get display name from available fields
              const displayName = userProfile?.name ||
                (userProfile?.firstName && userProfile?.lastName ? `${userProfile.firstName} ${userProfile.lastName}` : '') ||
                userProfile?.firstName ||
                userProfile?.lastName ||
                participant.userName ||
                'Unknown User';
              return {
                ...participant,
                displayName
              };
            } catch (err) {
              console.error('Error fetching user profile:', err);
              return {
                ...participant,
                displayName: participant.userName || 'Unknown User'
              };
            }
          })
        );
        setParticipantsWithNames(participantsWithDetails);
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      setError(err.message || 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  // Check if event has passed
  const isEventPassed = event?.date ? new Date(event.date) < new Date() : false;

  const handleParticipate = () => {
    if (!user) {
      alert('Please log in to participate in events');
      navigate('/login');
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmParticipation = async () => {
    try {
      setActionLoading(true);
      setShowConfirmDialog(false);
      await registerForEvent(id);
      setIsParticipating(true);
      await fetchEventDetails(); // Refresh event data
      alert('Successfully registered for the event!');
    } catch (err) {
      console.error('Error registering for event:', err);
      alert(err.message || 'Failed to register for event');
    } finally {
      setActionLoading(false);
    }
  };

  const cancelParticipation = () => {
    setShowConfirmDialog(false);
  };

  const handleRemoveParticipant = (userId, userName) => {
    setRemoveDialog({ show: true, userId, userName });
  };

  const confirmRemoveParticipant = async () => {
    try {
      setActionLoading(true);
      await removeParticipant(id, removeDialog.userId);
      setRemoveDialog({ show: false, userId: null, userName: '' });
      await fetchEventDetails(); // Refresh event data
      alert('Participant removed successfully');
    } catch (err) {
      console.error('Error removing participant:', err);
      alert(err.message || 'Failed to remove participant');
    } finally {
      setActionLoading(false);
    }
  };

  const cancelRemoveParticipant = () => {
    setRemoveDialog({ show: false, userId: null, userName: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateForCalendar = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const addToCalendar = () => {
    if (!event || !event.date) {
      alert('Event date is not available');
      return;
    }

    try {
      const startDate = new Date(event.date);
      const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // Default 2 hours duration

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//KOS Event//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `DTSTART:${formatDateForCalendar(startDate)}`,
        `DTEND:${formatDateForCalendar(endDate)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
        `LOCATION:${event.location || ''}`,
        `UID:${event.id}@kos-events`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error creating calendar event:', err);
      alert('Failed to create calendar event');
    }
  };

  if (loading) {
    return (
      <div className="page event-detail-page">
        <div className="loading">Loading event details...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="page event-detail-page">
        <Card>
          <div className="error-message">
            <h2>Error</h2>
            <p>{error || 'Event not found'}</p>
            <Button onClick={() => navigate('/events')}>Back to Events</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="page event-detail-page">
      <div className="event-detail-container">
        <Button
          onClick={() => navigate('/events')}
          className="back-button"
          variant="secondary"
        >
          ← Back to Events
        </Button>

        {/* Hero Section with Image and Title */}
        <div className="event-hero">
          {event.imageUrl && (
            <div className="event-hero-image">
              <img src={event.imageUrl} alt={event.title} />
              <div className="image-overlay"></div>
            </div>
          )}
          <div className="event-hero-content">
            <div className="event-meta-tags">
              {event.status && (
                <span className={`event-badge status-${event.status}`}>
                  {event.status}
                </span>
              )}
              {event.category && (
                <span className="event-badge category-badge">
                  {event.category}
                </span>
              )}
            </div>
            <h1 className="event-title">{event.title}</h1>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="event-content-grid">

          {/* Left Column - Main Info */}
          <div className="event-main-content">

            {/* Quick Info Cards */}
            <div className="quick-info-grid">
              <div className="quick-info-card">
                <div className="info-icon">📅</div>
                <div className="info-content">
                  <span className="info-label">Date & Time</span>
                  <span className="info-value">{formatDate(event.date)}</span>
                </div>
              </div>

              <div className="quick-info-card">
                <div className="info-icon">📍</div>
                <div className="info-content">
                  <span className="info-label">Location</span>
                  <span className="info-value">{event.location || 'TBD'}</span>
                </div>
              </div>

              <div className="quick-info-card">
                <div className="info-icon">👥</div>
                <div className="info-content">
                  <span className="info-label">Participants</span>
                  <span className="info-value">
                    {event.participants?.length || 0}
                    {event.maxParticipants && ` / ${event.maxParticipants}`}
                  </span>
                </div>
              </div>

              {event.points && (
                <div className="quick-info-card points-card">
                  <div className="info-icon">⭐</div>
                  <div className="info-content">
                    <span className="info-label">Reward Points</span>
                    <span className="info-value">{event.points} points</span>
                  </div>
                </div>
              )}
            </div>

            {/* Description Section */}
            <Card className="detail-section">
              <h2 className="section-title">About This Event</h2>
              <p className="event-description">
                {event.description || 'No description available'}
              </p>
            </Card>

            {/* Requirements Section */}
            {event.requirements && (
              <Card className="detail-section">
                <h2 className="section-title">Requirements</h2>
                <p className="section-content">{event.requirements}</p>
              </Card>
            )}

            {/* Admin: Participants List */}
            {isAdmin && participantsWithNames.length > 0 && (
              <Card className="detail-section participants-section">
                <h2 className="section-title">
                  Registered Participants ({participantsWithNames.length})
                </h2>
                <div className="participants-list">
                  {participantsWithNames.map((participant, index) => (
                    <div key={participant.userId || index} className="participant-card">
                      <div className="participant-avatar">
                        {participant.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div className="participant-details">
                        <div className="participant-name">{participant.displayName}</div>
                        {participant.registeredAt && (
                          <div className="participant-date">
                            Registered {new Date(participant.registeredAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        )}
                      </div>
                      <div className="participant-number">#{index + 1}</div>
                      <Button
                        onClick={() => handleRemoveParticipant(participant.userId, participant.displayName)}
                        variant="secondary"
                        className="remove-participant-button"
                        disabled={actionLoading}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Action Card */}
          <div className="event-sidebar">
            <Card className="action-card">
              <h3 className="action-card-title">Event Registration</h3>

              {isEventPassed ? (
                <div className="registration-disabled">
                  <div className="disabled-icon">🕐</div>
                  <p>This event has already passed. Registration is no longer available.</p>
                </div>
              ) : event.participationEnabled === false ? (
                <div className="registration-disabled">
                  <div className="disabled-icon">🔒</div>
                  <p>Registration is currently not available for this event.</p>
                </div>
              ) : isParticipating ? (
                <div className="registration-success">
                  <div className="success-icon">✓</div>
                  <p className="success-title">You're Registered!</p>
                  <p className="success-message">
                    See you at the event. Check your email for details.
                  </p>
                </div>
              ) : (
                <div className="registration-available">
                  {event.maxParticipants && event.participants?.length >= event.maxParticipants ? (
                    <div className="registration-full">
                      <div className="full-icon">⚠️</div>
                      <p>This event is currently full.</p>
                    </div>
                  ) : (
                    <>
                      <div className="registration-info">
                        <div className="availability-indicator">
                          <span className="indicator-dot"></span>
                          <span>Spots Available</span>
                        </div>
                      </div>
                      <Button
                        onClick={handleParticipate}
                        disabled={actionLoading}
                        className="register-button"
                      >
                        {actionLoading ? 'Processing...' : 'Register Now'}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {event.date && (
                <div className="calendar-section">
                  <Button
                    onClick={addToCalendar}
                    variant="secondary"
                    className="calendar-button"
                  >
                    <span className="button-icon">📅</span>
                    Add to Calendar
                  </Button>
                </div>
              )}
            </Card>

            {/* Event Stats Card */}
            <Card className="stats-card">
              <h3 className="stats-title">Event Stats</h3>
              <div className="stats-list">
                <div className="stat-item">
                  <span className="stat-label">Current Registrations</span>
                  <span className="stat-value">{event.participants?.length || 0}</span>
                </div>
                {event.maxParticipants && (
                  <div className="stat-item">
                    <span className="stat-label">Maximum Capacity</span>
                    <span className="stat-value">{event.maxParticipants}</span>
                  </div>
                )}
                <div className="stat-item">
                  <span className="stat-label">Status</span>
                  <span className="stat-value">{event.status || 'Upcoming'}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="dialog-overlay" onClick={cancelParticipation}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Participation</h2>
            <p>Are you sure you want to participate in "{event.title}"?</p>
            <div className="dialog-actions">
              <Button onClick={confirmParticipation} disabled={actionLoading}>
                Confirm
              </Button>
              <Button onClick={cancelParticipation} variant="secondary" disabled={actionLoading}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Participant Dialog */}
      {removeDialog.show && (
        <div className="dialog-overlay" onClick={cancelRemoveParticipant}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <h2>Remove Participant</h2>
            <p>Are you sure you want to remove <strong>{removeDialog.userName}</strong> from this event?</p>
            <div className="dialog-actions">
              <Button onClick={confirmRemoveParticipant} disabled={actionLoading} variant="primary">
                {actionLoading ? 'Removing...' : 'Remove'}
              </Button>
              <Button onClick={cancelRemoveParticipant} variant="secondary" disabled={actionLoading}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .event-detail-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
          background: #f8f9fa;
        }

        .event-detail-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .back-button {
          align-self: flex-start;
          font-weight: 500;
        }

        /* Hero Section */
        .event-hero {
          position: relative;
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .event-hero-image {
          position: relative;
          width: 100%;
          height: 400px;
          overflow: hidden;
        }

        .event-hero-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
        }

        .event-hero-content {
          padding: 2rem;
        }

        .event-meta-tags {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .event-badge {
          display: inline-block;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-upcoming {
          background: #e3f2fd;
          color: #1976d2;
        }

        .status-ongoing {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status-completed {
          background: #f5f5f5;
          color: #616161;
        }

        .category-badge {
          background: #f3e5f5;
          color: #7b1fa2;
        }

        .event-title {
          margin: 0;
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
          line-height: 1.2;
        }

        /* Content Grid */
        .event-content-grid {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 2rem;
          align-items: start;
        }

        /* Quick Info Grid */
        .quick-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .quick-info-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .quick-info-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .points-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .points-card .info-label,
        .points-card .info-value {
          color: white;
        }

        .info-icon {
          font-size: 2rem;
          line-height: 1;
        }

        .info-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          flex: 1;
        }

        .info-label {
          font-size: 0.85rem;
          color: #666;
          font-weight: 500;
        }

        .info-value {
          font-size: 1.1rem;
          color: #1a1a1a;
          font-weight: 600;
        }

        /* Detail Sections */
        .detail-section {
          margin-bottom: 1.5rem;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        .section-title {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .event-description,
        .section-content {
          margin: 0;
          color: #444;
          line-height: 1.8;
          font-size: 1.05rem;
          white-space: pre-wrap;
        }

        /* Participants Section */
        .participants-section {
          background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
        }

        .participants-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .participant-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          transition: all 0.2s;
        }

        .participant-card:hover {
          border-color: #1976d2;
          box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
        }

        .participant-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .participant-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .participant-name {
          font-weight: 600;
          color: #1a1a1a;
          font-size: 1rem;
        }

        .participant-date {
          font-size: 0.85rem;
          color: #666;
        }

        .participant-number {
          font-weight: 700;
          color: #999;
          font-size: 0.9rem;
        }

        .remove-participant-button {
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
          background: #dc3545;
          color: white;
          border: none;
        }

        .remove-participant-button:hover {
          background: #c82333;
        }

        .remove-participant-button:disabled {
          background: #e0e0e0;
          color: #999;
          cursor: not-allowed;
        }

        /* Sidebar */
        .event-sidebar {
          position: sticky;
          top: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        /* Action Card */
        .action-card {
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .action-card-title {
          margin: 0 0 1.5rem 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .registration-success {
          text-align: center;
          padding: 2rem 1rem;
        }

        .success-icon {
          width: 60px;
          height: 60px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          background: #4caf50;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: 700;
        }

        .success-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #4caf50;
          margin: 0 0 0.5rem 0;
        }

        .success-message {
          color: #666;
          margin: 0;
        }

        .registration-disabled,
        .registration-full {
          text-align: center;
          padding: 2rem 1rem;
        }

        .disabled-icon,
        .full-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .registration-disabled p,
        .registration-full p {
          color: #666;
          margin: 0;
        }

        .registration-available {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .registration-info {
          padding: 1rem;
          background: #f0f7ff;
          border-radius: 8px;
          border-left: 4px solid #1976d2;
        }

        .availability-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          color: #1976d2;
        }

        .indicator-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #4caf50;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .register-button {
          width: 100%;
          padding: 1rem;
          font-size: 1.1rem;
          font-weight: 600;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          transition: transform 0.2s;
        }

        .register-button:hover {
          transform: scale(1.02);
        }

        .calendar-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e0e0e0;
        }

        .calendar-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .button-icon {
          font-size: 1.2rem;
        }

        /* Stats Card */
        .stats-card {
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        .stats-title {
          margin: 0 0 1rem 0;
          font-size: 1.1rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .stats-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f0f0f0;
        }

        .stat-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .stat-label {
          color: #666;
          font-size: 0.9rem;
        }

        .stat-value {
          font-weight: 700;
          color: #1a1a1a;
          font-size: 1rem;
        }

        /* Dialog */
        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .dialog-content {
          background: white;
          padding: 2.5rem;
          border-radius: 16px;
          max-width: 450px;
          width: 90%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .dialog-content h2 {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
          color: #1a1a1a;
        }

        .dialog-content p {
          margin: 0 0 1.5rem 0;
          color: #666;
          line-height: 1.6;
        }

        .dialog-actions {
          display: flex;
          gap: 1rem;
        }

        .dialog-actions button {
          flex: 1;
          padding: 0.75rem;
          font-weight: 600;
        }

        /* Error & Loading States */
        .error-message {
          text-align: center;
          padding: 3rem 2rem;
        }

        .error-message h2 {
          color: #d32f2f;
          margin: 0 0 1rem 0;
        }

        .error-message p {
          color: #666;
          margin-bottom: 1.5rem;
        }

        .loading {
          text-align: center;
          padding: 4rem 2rem;
          font-size: 1.2rem;
          color: #666;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .event-content-grid {
            grid-template-columns: 1fr;
          }

          .event-sidebar {
            position: static;
          }

          .quick-info-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .event-detail-page {
            padding: 1rem;
          }

          .event-hero-image {
            height: 250px;
          }

          .event-hero-content {
            padding: 1.5rem;
          }

          .event-title {
            font-size: 1.75rem;
          }

          .quick-info-grid {
            grid-template-columns: 1fr;
          }

          .detail-section {
            padding: 1.5rem;
          }

          .section-title {
            font-size: 1.25rem;
          }

          .action-card {
            padding: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .event-title {
            font-size: 1.5rem;
          }

          .quick-info-card {
            padding: 1rem;
          }

          .info-icon {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EventDetail;
